import { Drawer } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { SearchIconStroke, ModalCloseIcon } from '../../../../../assets/icons/common/common-icons';
import { LogoLoader } from '../../../../../common/components/LogoLoader/LogoLoader';
import { createFuzzySearch } from '../../../../../common/utils/fuzzy-search.utils';
import { useStore } from '../../../../app/data/root.store';
import { CheckboxCheckmarkIcon, CheckmarkPartialIcon, ArrowRightIcon } from './icons';
import {
    AllTagsWrapper,
    DrawerBody,
    DrawerCloseButton,
    DrawerHeader,
    DrawerTitle,
    DrawerWrapper,
    SearchIcon,
    SearchInput,
    SearchInputWrapper,
    SearchSection,
    TagOption,
    TagOptionContent,
    TagOptionIcon,
    TagOptionText,
    TagSection,
    TagSectionTitle
} from '../../VaultMetadataPanel/MetadataLabelsSection/TagsTagCategory/TagEditorDrawer.styles';


export const PeriodFilterDrawer = observer(({
    isOpen,
    onClose,
    selectedPeriods = [],
    onPeriodSelect,
}) => {
    const { vaultStore } = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedYears, setExpandedYears] = useState(new Set(['2025']));
    const [localSelectedPeriods, setLocalSelectedPeriods] = useState(new Set());

    const availablePeriods = useMemo(() => 
        vaultStore.userPeriods?.length > 0 ? vaultStore.userPeriods.filter(period => period.label && period.label.trim()) : []
    , [vaultStore.userPeriods]);

    useEffect(() => {
        if (isOpen && vaultStore.userPeriods.length === 0) {
            vaultStore.fetchUserPeriods();
        }
    }, [isOpen, vaultStore]);

    useEffect(() => {
        if (selectedPeriods && selectedPeriods.length > 0) {
            setLocalSelectedPeriods(new Set(selectedPeriods.map(p => p.key || p.id || p.name)));
        } else {
            setLocalSelectedPeriods(new Set());
        }
    }, [selectedPeriods]);

    const filteredData = useMemo(() => {
        if (!searchTerm || !availablePeriods.length) return availablePeriods;

        const yearFuzzySearch = createFuzzySearch(availablePeriods, ['label'], { threshold: 0.3 });
        const matchingYears = yearFuzzySearch(searchTerm);

        const allQuarters = availablePeriods.flatMap(year => 
            (year.quarters || []).map(quarter => ({ ...quarter, parentYear: year }))
        );
        
        const quarterFuzzySearch = createFuzzySearch(allQuarters, ['label'], { threshold: 0.3 });
        const matchingQuarters = quarterFuzzySearch(searchTerm);

        const yearKeys = new Set(matchingYears.map(y => y.key));
        const quarterParentKeys = new Set(matchingQuarters.map(q => q.parentYear.key));
        
        return availablePeriods.map(year => {
            const yearMatches = yearKeys.has(year.key);
            const hasMatchingQuarters = quarterParentKeys.has(year.key);
            
            if (yearMatches || hasMatchingQuarters) {
                const quarters = year.quarters || [];
                const relevantQuarters = hasMatchingQuarters && !yearMatches 
                    ? matchingQuarters
                        .filter(q => q.parentYear.key === year.key)
                        .map(q => ({ key: q.key, label: q.label }))
                    : quarters;
                    
                return {
                    ...year,
                    quarters: relevantQuarters
                };
            }
            return null;
        }).filter(Boolean);
    }, [availablePeriods, searchTerm]);

    const totalUniquePeriods = filteredData.reduce((count, year) => {
        const yearCount = year.quarters && year.quarters.length > 0 ? year.quarters.length : 1;
        return count + yearCount;
    }, 0);

    const isCollapsedMode = totalUniquePeriods > (vaultStore.periodsExpandedThreshold || 10);
    const isFlattenedMode = !isCollapsedMode;

    const getQuartersForYear = (yearKey) => {
        const year = availablePeriods.find(y => y.key === yearKey);
        return year ? year.quarters?.map(q => q.key) : [];
    };

    const areAllQuartersSelected = (yearKey) => {
        const year = availablePeriods.find(y => y.key === yearKey);
        if (year && year.type === 'no-period-files') {
            return localSelectedPeriods.has(yearKey);
        }

        const quarters = getQuartersForYear(yearKey);

        if (!quarters || quarters.length === 0) {
            return localSelectedPeriods.has(yearKey);
        }

        if (isFlattenedMode) {
            return localSelectedPeriods.has(yearKey);
        }

        return quarters.every(qKey => localSelectedPeriods.has(qKey)) && localSelectedPeriods.has(yearKey);
    };

    const areAnyQuartersSelected = (yearKey) => {
        const year = availablePeriods.find(y => y.key === yearKey);
        if (year && year.type === 'no-period-files') {
            return localSelectedPeriods.has(yearKey);
        }
        if (isFlattenedMode) {
            return false;
        }
        const quarters = getQuartersForYear(yearKey);
        return quarters?.length > 0 && quarters.some(qKey => localSelectedPeriods.has(qKey));
    };

    const swipeHandlers = useSwipeable({
        onSwipedDown: () => onClose(),
        trackMouse: false
    });

    const toggleYear = (yearKey) => {
        const newExpanded = new Set(expandedYears);
        if (newExpanded.has(yearKey)) {
            newExpanded.delete(yearKey);
        } else {
            newExpanded.add(yearKey);
        }
        setExpandedYears(newExpanded);
    };

    const handleYearSelection = (yearKey) => {
        const newSelected = new Set(localSelectedPeriods);
        const year = availablePeriods.find(y => y.key === yearKey);

        if (year && year.type === 'no-period-files') {
            if (newSelected.has(yearKey)) {
                newSelected.delete(yearKey);
            } else {
                newSelected.add(yearKey);
            }
            setLocalSelectedPeriods(newSelected);
            onPeriodSelect(year);
            return;
        }

        const quarters = getQuartersForYear(yearKey);

        if (!quarters || quarters.length === 0) {
            if (newSelected.has(yearKey)) {
                newSelected.delete(yearKey);
            } else {
                newSelected.add(yearKey);
            }
            setLocalSelectedPeriods(newSelected);
            onPeriodSelect(year);
            return;
        }

        if (isFlattenedMode) {
            if (newSelected.has(yearKey)) {
                newSelected.delete(yearKey);
            } else {
                newSelected.add(yearKey);
            }
            setLocalSelectedPeriods(newSelected);
            onPeriodSelect(year);
            return;
        }

        if (areAllQuartersSelected(yearKey)) {
            quarters.forEach(qKey => newSelected.delete(qKey));
            newSelected.delete(yearKey);
        } else {
            const unselectedQuarters = quarters.filter(qKey => !localSelectedPeriods.has(qKey));
            unselectedQuarters.forEach(qKey => newSelected.add(qKey));
            newSelected.add(yearKey);
        }

        setLocalSelectedPeriods(newSelected);
        onPeriodSelect(year);

        quarters.forEach(qKey => {
            const quarter = year.quarters?.find(q => q.key === qKey);
            if (quarter) {
                const wasSelected = localSelectedPeriods.has(qKey);
                const isNowSelected = newSelected.has(qKey);

                if (wasSelected !== isNowSelected) {
                    onPeriodSelect(quarter);
                }
            }
        });
    };

    const handleQuarterSelection = (quarterKey) => {
        const newSelected = new Set(localSelectedPeriods);
        const quarter = availablePeriods.flatMap(y => y.quarters).find(q => q.key === quarterKey);

        if (newSelected.has(quarterKey)) {
            newSelected.delete(quarterKey);
        } else {
            newSelected.add(quarterKey);
        }

        setLocalSelectedPeriods(newSelected);

        if (quarter) {
            onPeriodSelect(quarter);
        }
    };

    if (vaultStore.isLoadingPeriods) {
        return (
            <Drawer
                closable={false}
                visible={isOpen}
                height={'auto'}
                placement="bottom"
                onClose={onClose}
                className="tag-editor-drawer-mobile"
                destroyOnClose={true}
            >
                <DrawerWrapper {...swipeHandlers}>
                    <DrawerHeader>
                        <DrawerTitle>Period</DrawerTitle>
                        <DrawerCloseButton onClick={onClose}>
                            <ModalCloseIcon />
                        </DrawerCloseButton>
                    </DrawerHeader>
                    <DrawerBody>
                        <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                            <LogoLoader />
                        </div>
                    </DrawerBody>
                </DrawerWrapper>
            </Drawer>
        );
    }

    return (
        <Drawer
            closable={false}
            visible={isOpen}
            placement="bottom"
            onClose={onClose}
            className="tag-editor-drawer-mobile"
            destroyOnClose={true}
        >
            <DrawerWrapper {...swipeHandlers}>
                <DrawerHeader>
                    <DrawerTitle>Period</DrawerTitle>
                    <DrawerCloseButton onClick={onClose}>
                        <ModalCloseIcon />
                    </DrawerCloseButton>
                </DrawerHeader>

                <DrawerBody>
                    <SearchSection>
                        <SearchInputWrapper>
                            <SearchIcon>
                                <SearchIconStroke />
                            </SearchIcon>
                            <SearchInput
                                type="text"
                                placeholder="Search period"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </SearchInputWrapper>
                    </SearchSection>

                    <TagSection>
                        <TagSectionTitle>Periods</TagSectionTitle>
                        <AllTagsWrapper>
                            {filteredData.map((year) => (
                                <div key={year.key} style={{ marginBottom: '8px' }}>
                                    {!(isFlattenedMode && year.quarters?.length > 0 && year.type === 'year' && !year.hasDirectFiles) && (
                                        <TagOption onClick={() => handleYearSelection(year.key)}>
                                            <TagOptionContent>
                                                <TagOptionIcon style={{ 
                                                    width: '20px', 
                                                    height: '20px', 
                                                    border: '2px solid #9097A3',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: areAllQuartersSelected(year.key) ? '#1A1B1D' : 'transparent'
                                                }}>
                                                    {areAllQuartersSelected(year.key) && <CheckboxCheckmarkIcon />}
                                                    {!areAllQuartersSelected(year.key) && areAnyQuartersSelected(year.key) && <CheckmarkPartialIcon />}
                                                </TagOptionIcon>
                                                <TagOptionText style={{ flex: 1 }}>{year.label}</TagOptionText>
                                                {year.quarters?.length > 0 && isCollapsedMode && year.type !== 'no-period-files' && (
                                                    <div 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleYear(year.key);
                                                        }}
                                                        style={{
                                                            transform: expandedYears.has(year.key) ? 'rotate(90deg)' : '',
                                                            transition: 'transform 0.3s ease',
                                                            cursor: 'pointer',
                                                            padding: '4px'
                                                        }}
                                                    >
                                                        <ArrowRightIcon />
                                                    </div>
                                                )}
                                            </TagOptionContent>
                                        </TagOption>
                                    )}

                                    {(expandedYears.has(year.key) || isFlattenedMode) && year.quarters?.map(quarter => (
                                        <TagOption
                                            key={quarter.key}
                                            onClick={() => handleQuarterSelection(quarter.key)}
                                            style={{ marginLeft: isCollapsedMode ? '20px' : '0', marginTop: '4px' }}
                                        >
                                            <TagOptionContent>
                                                <TagOptionIcon style={{ 
                                                    width: '20px', 
                                                    height: '20px', 
                                                    border: '2px solid #9097A3',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: localSelectedPeriods.has(quarter.key) ? '#1A1B1D' : 'transparent'
                                                }}>
                                                    {localSelectedPeriods.has(quarter.key) && <CheckboxCheckmarkIcon />}
                                                </TagOptionIcon>
                                                <TagOptionText>{quarter.label}</TagOptionText>
                                            </TagOptionContent>
                                        </TagOption>
                                    ))}
                                </div>
                            ))}
                        </AllTagsWrapper>
                    </TagSection>
                </DrawerBody>
            </DrawerWrapper>
        </Drawer>
    );
}); 